import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Shield, Lock, Settings, Users, Facebook, Instagram, Linkedin, Youtube, Menu, X, ExternalLink, Gift,
  Smartphone, Monitor, Wifi, Camera, Home as HomeIcon, Tv, Cloud, Mail, Car, ChevronDown, ArrowRight,
  Scan, CheckCircle2, AlertTriangle, Zap, RotateCcw, Quote, Lightbulb, Check, Play, Pause, Clock, GripHorizontal
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AnimatedLogo from '../components/AnimatedLogo';
import ScamIQBadge from '../components/ScamIQBadge';
import FamilyProductsSection from '../components/FamilyProductsSection';
import PerthPropertyPulseTile from '../components/PerthPropertyPulseTile';
import SEO from '../components/SEO';
import { trackLead, trackContact } from '../utils/metaPixel';
import { CyberForestHuntModal, CyberForestHuntBanner, useCyberForestHuntTrigger } from '../components/CyberForestHunt';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import axios from 'axios';
import { API_URL } from '../config';
import '../styles/Home.css';
import '../styles/ConnectedCarousel.css';

const API = `${API_URL}/api`;

// Section ID to route mapping for URL updates
const SECTION_TO_ROUTE = {
  'hero': '/first-step',
  'intro': '/baseline',
  'connected-world': '/your-stack',
  'courses': '/courses',  // This goes to the dedicated courses page
  'tools': '/tools',
  'testimonials': '/testimonials',
  'cta': '/safety-check',
  'social': '/social',
  'contact': '/contact'
};

// Route to section ID mapping
const ROUTE_TO_SECTION = {
  '/': 'hero',
  '/first-step': 'hero',
  '/baseline': 'intro',
  '/your-stack': 'connected-world',
  '/tools': 'tools',
  '/testimonials': 'testimonials',
  '/safety-check': 'cta',
  '/social': 'social',
  '/contact': 'contact'
};

const Home = ({ initialSection }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [activeToolsTab, setActiveToolsTab] = useState('browser');
  const [showMoreBrowser, setShowMoreBrowser] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);
  const [keywordAnimationTriggered, setKeywordAnimationTriggered] = useState(false);
  const introSectionRef = useRef(null);
  const heroVideoRef = useRef(null);
  
  // Force video to play immediately on mount (bypasses Safari's play button)
  useEffect(() => {
    const video = heroVideoRef.current;
    if (video) {
      video.play().catch(() => {
        // Autoplay blocked - will show first frame which is fine
      });
    }
  }, []);
  
  // Digital Risk X-Ray Mini-App State
  const [xrayActive, setXrayActive] = useState(false);
  const [xrayStep, setXrayStep] = useState(0);
  const [xrayAnswers, setXrayAnswers] = useState({});
  const [xrayComplete, setXrayComplete] = useState(false);
  const [xrayResults, setXrayResults] = useState(null);

  // Feature flag for Cyber Forest Hunt (set to true to enable)
  const CYBER_HUNT_ENABLED = true;
  
  // Cyber Forest Hunt trigger hook
  const {
    shouldShow: showHuntModal,
    showBanner: showHuntBanner,
    closeModal: closeHuntModal,
    reopenModal: reopenHuntModal,
    closeBanner: closeHuntBanner,
    onCTAClick: onHuntCTAClick,
    markAsCompleted: markHuntCompleted,
    hasCompleted: hasCompletedHunt,
  } = useCyberForestHuntTrigger({ enabled: CYBER_HUNT_ENABLED });

  // Device Track Carousel data - 9 devices
  const trackDevices = [
    {
      id: 'phone',
      label: 'Phone',
      icon: Smartphone,
      teaser: 'Stop silent tracking',
      benefit: 'Stop silent tracking.',
      painLine: 'Most phones quietly accumulate permissions and sharing — until you tighten them.',
      bullets: ['Clean up permissions', 'Reduce background access', 'Strengthen account security'],
      didYouKnow: 'Apps can keep access you granted months ago.',
      isRelevant: true
    },
    {
      id: 'laptop',
      label: 'Laptop & Desktop',
      icon: Monitor,
      teaser: 'Reduce everyday risk',
      benefit: 'Reduce everyday risk.',
      painLine: 'Small defaults in updates, access, and browsing create \'easy win\' openings.',
      bullets: ['Lock down access', 'Safer browsing settings', 'Keep protections current'],
      didYouKnow: 'Most attacks rely on basic settings being left untouched.',
      isRelevant: true
    },
    {
      id: 'wifi',
      label: 'Home Wi-Fi & Router',
      icon: Wifi,
      teaser: 'Secure your network',
      benefit: 'Secure the front door of your home.',
      painLine: 'If router settings stay default, every connected device inherits the weakness.',
      bullets: ['Stronger router admin settings', 'Safer Wi-Fi configuration', 'Confirm it\'s truly locked down'],
      didYouKnow: 'A safer router doesn\'t mean slower internet.',
      isRelevant: false
    },
    {
      id: 'cameras',
      label: 'Cameras & Doorbells',
      icon: Camera,
      teaser: 'Keep feeds private',
      benefit: 'Keep your home feed private.',
      painLine: 'Remote access and weak logins are the usual entry points — not the camera itself.',
      bullets: ['Secure logins', 'Tighten remote access', 'Safer device configuration'],
      didYouKnow: 'Many systems expose access through old sharing settings.',
      isRelevant: false
    },
    {
      id: 'smart-home',
      label: 'Smart Home Devices',
      icon: HomeIcon,
      teaser: 'Control connections',
      benefit: 'Control what connects — and what doesn\'t.',
      painLine: 'Smart devices often collect more than needed unless you restrict them.',
      bullets: ['Limit permissions', 'Reduce unnecessary access', 'Make weak links stronger'],
      didYouKnow: 'Some devices talk to services you never use.',
      isRelevant: false
    },
    {
      id: 'tv',
      label: 'Smart TV & Streaming',
      icon: Tv,
      teaser: 'Cut data sharing',
      benefit: 'Cut down data sharing on shared devices.',
      painLine: 'Shared screens are often the noisiest for tracking and account leakage.',
      bullets: ['Reduce tracking', 'Tighten privacy options', 'Clean connected accounts'],
      didYouKnow: 'TVs often collect usage data by default.',
      isRelevant: false
    },
    {
      id: 'cloud',
      label: 'Cloud Accounts',
      icon: Cloud,
      teaser: 'Protect your files',
      benefit: 'Protect what matters most.',
      painLine: 'One risky share link or weak recovery path can expose years of files.',
      bullets: ['Safer sharing', 'Stronger access controls', 'Better recovery settings'],
      didYouKnow: 'Old shares often stay active until you revoke them.',
      isRelevant: true
    },
    {
      id: 'email',
      label: 'Email & Messages',
      icon: Mail,
      teaser: 'Stop scams early',
      benefit: 'Stop scams before they land.',
      painLine: 'Most phishing succeeds because it looks routine — not obviously suspicious.',
      bullets: ['Recognise scam patterns', 'Safer account settings', 'Simple checking habits'],
      didYouKnow: 'Scams often mimic real brands perfectly.',
      isRelevant: true
    },
    {
      id: 'car',
      label: 'Connected Car Apps',
      icon: Car,
      teaser: 'Protect location',
      benefit: 'Protect location and account access.',
      painLine: 'Convenience features can share more than you realise unless you adjust them.',
      bullets: ['Tighten sharing', 'Limit permissions', 'Safer connected features'],
      didYouKnow: 'Location and access sharing can persist across upgrades.',
      isRelevant: false
    }
  ];

  // ============================================
  // 3D PERSPECTIVE CAROUSEL - Premium Implementation
  // ============================================
  
  const totalCards = trackDevices.length;
  const anglePerCard = 360 / totalCards;
  
  // Core carousel state
  const [carouselRotation, setCarouselRotation] = useState(0);
  const [activeCardIndex, setActiveCardIndex] = useState(null);
  const [isSnapping, setIsSnapping] = useState(false);
  const [showHint, setShowHint] = useState(true);
  const [hintDevice, setHintDevice] = useState('Phone');
  const [isDragging, setIsDragging] = useState(false);
  const [showDragHint, setShowDragHint] = useState(true);
  const [snapCardIndex, setSnapCardIndex] = useState(null);
  
  // Refs for animation and interaction
  const carouselRef = useRef(null);
  const stateRef = useRef({
    rotation: 0,
    velocity: 0,
    pointerDown: false,      // Is pointer currently down?
    confirmedDrag: false,    // Has movement exceeded threshold? (true = drag, false = potential click)
    isSnapping: false,
    lastPointerX: 0,
    lastTime: 0,
    dragStartX: 0,
    dragStartRotation: 0,
    totalDragDistance: 0,
    autoplayPaused: false,
    lastInteraction: 0,
    hasInteracted: false,
    pointerId: null,
    pointerCaptured: false
  });
  const animationRef = useRef(null);
  const snapTimeoutRef = useRef(null);
  const autoplayTimeoutRef = useRef(null);
  
  // Premium configuration - tuned for Mercedes-smooth feel
  const config = {
    perspective: 1400,
    radius: 420,
    rotateYMax: 55,
    scaleMin: 0.72,
    scaleMax: 1.15,
    zMin: -220,
    friction: 0.96, // Higher = smoother coast (was 0.92)
    snapThreshold: 0.08, // Lower = snaps sooner when slow
    dragSensitivity: 0.28, // Slightly less sensitive for control
    hysteresisThreshold: 5, // Lower for responsiveness
    snapIdleDelay: 400,
    autoplaySpeed: 0.008,
    autoplayResumeDelay: 8000,
    snapDuration: 400,
    snapEasing: (t) => 1 - Math.pow(1 - t, 4), // Ease out quart - even smoother
    velocitySmoothing: 0.25, // Blend factor for velocity (lower = smoother)
    maxVelocity: 25, // Cap velocity for control
    minVelocityForMomentum: 0.5 // Minimum velocity to trigger momentum
  };
  
  // Check for reduced motion preference
  const prefersReducedMotion = typeof window !== 'undefined' && 
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  // Hint rotation
  useEffect(() => {
    if (!showHint) return;
    const hints = ['Phone', 'Email', 'Cloud', 'Laptop'];
    let index = 0;
    const interval = setInterval(() => {
      index = (index + 1) % hints.length;
      setHintDevice(hints[index]);
    }, 3000);
    return () => clearInterval(interval);
  }, [showHint]);
  
  // Get closest card index from rotation
  const getClosestCardIndex = (rotation) => {
    const normalizedRotation = ((rotation % 360) + 360) % 360;
    const index = Math.round(normalizedRotation / anglePerCard) % totalCards;
    return index;
  };
  
  // Calculate card transform for a given rotation - Premium depth layering
  const getCardTransform = (cardIndex, currentRotation, isActive) => {
    const cardAngle = cardIndex * anglePerCard;
    const relativeAngle = ((cardAngle - currentRotation) % 360 + 540) % 360 - 180;
    
    // Normalize to -180 to 180
    const normalizedAngle = relativeAngle;
    const absAngle = Math.abs(normalizedAngle);
    
    // X position on the arc
    const x = Math.sin(normalizedAngle * Math.PI / 180) * config.radius;
    
    // Z depth - cards further from center go back more dramatically
    const depthFactor = Math.pow(absAngle / 90, 1.2); // Non-linear depth
    const zBase = Math.cos(normalizedAngle * Math.PI / 180) * config.radius - config.radius;
    const z = Math.max(zBase * (1 + depthFactor * 0.3), config.zMin);
    
    // Y rotation - cards rotate to face center with premium curve
    const rotateY = Math.max(-config.rotateYMax, Math.min(config.rotateYMax, normalizedAngle * 0.6));
    
    // Scale - premium depth with larger center card
    const scaleProgress = 1 - Math.min(absAngle / 80, 1);
    const baseScale = config.scaleMin + (1 - config.scaleMin) * Math.pow(scaleProgress, 0.8);
    const scale = isActive ? config.scaleMax : baseScale;
    
    // Opacity - smoother falloff for distant cards
    const opacityProgress = 1 - Math.min(absAngle / 110, 1);
    const opacity = Math.max(0.2, Math.pow(opacityProgress, 0.7));
    
    // Brightness - premium lighting effect
    const brightnessBase = isActive ? 1.12 : 1 - absAngle / 280;
    const brightness = Math.max(0.65, brightnessBase);
    
    // Blur for depth (very subtle, never on text)
    const blur = absAngle > 60 ? Math.min((absAngle - 60) / 80, 1) * 2 : 0;
    
    return {
      x,
      z,
      rotateY,
      scale,
      opacity,
      brightness,
      blur,
      isCenter: absAngle < 18
    };
  };
  
  // Main animation loop
  useEffect(() => {
    if (prefersReducedMotion) return;
    
    let lastTime = performance.now();
    
    const animate = (currentTime) => {
      const deltaTime = Math.min((currentTime - lastTime) / 16.67, 2); // Normalize to ~60fps
      lastTime = currentTime;
      
      const state = stateRef.current;
      
      // Skip if snapping (handled by snap animation)
      if (state.isSnapping) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }
      
      // Apply velocity with friction when not actively dragging
      if (!state.pointerDown && Math.abs(state.velocity) > 0.001) {
        state.velocity *= config.friction;
        state.rotation += state.velocity * deltaTime;
        
        // Check if should snap
        if (Math.abs(state.velocity) < config.snapThreshold && activeCardIndex === null) {
          // Start snap timer
          if (!snapTimeoutRef.current) {
            snapTimeoutRef.current = setTimeout(() => {
              initiateSnap();
            }, config.snapIdleDelay);
          }
        }
        
        setCarouselRotation(state.rotation);
      }
      
      // Autoplay when idle (only if no active card)
      if (!state.pointerDown && activeCardIndex === null && !state.autoplayPaused) {
        const timeSinceInteraction = currentTime - state.lastInteraction;
        if (timeSinceInteraction > config.autoplayResumeDelay && Math.abs(state.velocity) < 0.01) {
          state.rotation += config.autoplaySpeed * deltaTime;
          setCarouselRotation(state.rotation);
        }
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [activeCardIndex, prefersReducedMotion]);
  
  // Smooth snap to nearest card with magnetic effect
  const initiateSnap = useCallback(() => {
    if (snapTimeoutRef.current) {
      clearTimeout(snapTimeoutRef.current);
      snapTimeoutRef.current = null;
    }
    
    const state = stateRef.current;
    const startRotation = state.rotation;
    const targetIndex = getClosestCardIndex(startRotation);
    const targetRotation = targetIndex * anglePerCard;
    
    // Find shortest path
    let diff = targetRotation - (startRotation % 360);
    if (diff > 180) diff -= 360;
    if (diff < -180) diff += 360;
    
    const endRotation = startRotation + diff;
    const duration = config.snapDuration;
    const startTime = performance.now();
    
    state.isSnapping = true;
    state.velocity = 0;
    setIsSnapping(true);
    
    // Trigger snap ping effect
    setSnapCardIndex(targetIndex);
    setTimeout(() => setSnapCardIndex(null), 500);
    
    const snapAnimate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = config.snapEasing(progress);
      
      state.rotation = startRotation + (endRotation - startRotation) * easedProgress;
      setCarouselRotation(state.rotation);
      
      if (progress < 1) {
        requestAnimationFrame(snapAnimate);
      } else {
        state.isSnapping = false;
        setIsSnapping(false);
      }
    };
    
    requestAnimationFrame(snapAnimate);
  }, [anglePerCard, config.snapDuration, config.snapEasing]);
  
  // ============================================
  // POINTER EVENT HANDLERS - Mercedes-smooth Implementation
  // Key fix: Proper drag threshold with immediate 1:1 tracking
  // ============================================
  
  const DRAG_THRESHOLD = 8; // Pixels before we consider it a drag (not a click)
  
  const handlePointerDown = (e) => {
    // Only handle left mouse button or touch
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    
    const state = stateRef.current;
    
    // Cancel any pending snap
    if (snapTimeoutRef.current) {
      clearTimeout(snapTimeoutRef.current);
      snapTimeoutRef.current = null;
    }
    
    // Stop any ongoing momentum immediately for responsive feel
    state.velocity = 0;
    state.isSnapping = false;
    
    // Initialize interaction state
    state.pointerDown = true;
    state.confirmedDrag = false; // Key: we don't know if it's a drag yet
    state.lastPointerX = e.clientX;
    state.lastTime = performance.now();
    state.dragStartX = e.clientX;
    state.dragStartRotation = state.rotation;
    state.totalDragDistance = 0;
    state.autoplayPaused = true;
    state.lastInteraction = performance.now();
    state.pointerId = e.pointerId;
    state.pointerCaptured = false;
    
    // For touch: capture immediately to prevent scroll interference
    if (e.pointerType === 'touch' && carouselRef.current) {
      try {
        carouselRef.current.setPointerCapture(e.pointerId);
        state.pointerCaptured = true;
      } catch (err) {
        // Silently fail if capture not supported
      }
    }
    
    // Hide hints on first interaction
    if (!state.hasInteracted) {
      state.hasInteracted = true;
      setShowDragHint(false);
    }
    setShowHint(false);
  };
  
  const handlePointerMove = (e) => {
    const state = stateRef.current;
    if (!state.pointerDown) return;
    
    const currentX = e.clientX;
    const deltaX = currentX - state.lastPointerX;
    const totalDeltaX = currentX - state.dragStartX;
    const now = performance.now();
    const deltaTime = Math.max(now - state.lastTime, 1);
    
    state.totalDragDistance = Math.abs(totalDeltaX);
    
    // Check if we've crossed the drag threshold
    if (!state.confirmedDrag && state.totalDragDistance > DRAG_THRESHOLD) {
      // NOW we know this is a drag, not a click
      state.confirmedDrag = true;
      setIsDragging(true);
      
      // Capture pointer for mouse (already captured for touch)
      if (e.pointerType === 'mouse' && carouselRef.current && !state.pointerCaptured) {
        try {
          carouselRef.current.setPointerCapture(state.pointerId);
          state.pointerCaptured = true;
        } catch (err) {}
      }
      
      // Close detail panel when starting to drag
      if (activeCardIndex !== null) {
        setActiveCardIndex(null);
      }
    }
    
    // Only apply rotation once we've confirmed it's a drag
    if (state.confirmedDrag) {
      // Prevent default to stop any browser handling
      e.preventDefault();
      
      // Calculate velocity with smoothing for buttery feel
      const instantVelocity = (deltaX * config.dragSensitivity) / deltaTime * 16.67;
      const clampedVelocity = Math.max(-config.maxVelocity, Math.min(config.maxVelocity, instantVelocity));
      state.velocity = state.velocity * (1 - config.velocitySmoothing) + clampedVelocity * config.velocitySmoothing;
      
      // Update rotation directly - 1:1 tracking for Mercedes feel
      state.rotation -= deltaX * config.dragSensitivity;
      setCarouselRotation(state.rotation);
    }
    
    state.lastPointerX = currentX;
    state.lastTime = now;
  };
  
  const handlePointerUp = (e) => {
    const state = stateRef.current;
    if (!state.pointerDown) return;
    
    const wasDrag = state.confirmedDrag;
    
    // Reset pointer state
    state.pointerDown = false;
    state.confirmedDrag = false;
    state.lastInteraction = performance.now();
    setIsDragging(false);
    
    // Release pointer capture
    if (carouselRef.current && state.pointerCaptured) {
      try {
        carouselRef.current.releasePointerCapture(state.pointerId);
      } catch (err) {}
    }
    state.pointerCaptured = false;
    
    // If this wasn't a drag (total movement < threshold), it was a CLICK
    // The card's onClick handler will fire naturally - don't interfere
    if (!wasDrag) {
      state.velocity = 0;
      return;
    }
    
    // This was a drag - apply momentum or snap
    const timeSinceLast = performance.now() - state.lastTime;
    
    if (timeSinceLast < 100 && Math.abs(state.velocity) > config.minVelocityForMomentum) {
      // Flick gesture - boost velocity for satisfying momentum
      state.velocity *= 1.5;
      state.velocity = Math.max(-config.maxVelocity * 1.5, Math.min(config.maxVelocity * 1.5, state.velocity));
    } else if (Math.abs(state.velocity) < config.minVelocityForMomentum) {
      // Slow release - snap to nearest card
      state.velocity = 0;
      initiateSnap();
    }
    
    // Resume autoplay after delay
    if (autoplayTimeoutRef.current) clearTimeout(autoplayTimeoutRef.current);
    autoplayTimeoutRef.current = setTimeout(() => {
      stateRef.current.autoplayPaused = false;
    }, config.autoplayResumeDelay);
  };
  
  // Handle pointer cancel (important for touch)
  const handlePointerCancel = (e) => {
    const state = stateRef.current;
    state.pointerDown = false;
    state.confirmedDrag = false;
    state.velocity = 0;
    setIsDragging(false);
    
    if (carouselRef.current && state.pointerCaptured) {
      try {
        carouselRef.current.releasePointerCapture(state.pointerId);
      } catch (err) {}
    }
    state.pointerCaptured = false;
  };
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const state = stateRef.current;
        state.lastInteraction = performance.now();
        state.autoplayPaused = true;
        
        const direction = e.key === 'ArrowLeft' ? -1 : 1;
        const currentIndex = getClosestCardIndex(state.rotation);
        const targetIndex = (currentIndex + direction + totalCards) % totalCards;
        
        selectCard(targetIndex);
        
        // Resume autoplay after delay
        if (autoplayTimeoutRef.current) clearTimeout(autoplayTimeoutRef.current);
        autoplayTimeoutRef.current = setTimeout(() => {
          stateRef.current.autoplayPaused = false;
        }, config.autoplayResumeDelay);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [totalCards]);
  
  // Select a specific card with animation
  const selectCard = (index) => {
    const state = stateRef.current;
    
    if (snapTimeoutRef.current) {
      clearTimeout(snapTimeoutRef.current);
      snapTimeoutRef.current = null;
    }
    
    const startRotation = state.rotation;
    const targetRotation = index * anglePerCard;
    
    // Find shortest path
    let diff = targetRotation - (startRotation % 360);
    if (diff > 180) diff -= 360;
    if (diff < -180) diff += 360;
    
    const endRotation = startRotation + diff;
    const duration = 500;
    const startTime = performance.now();
    
    state.isSnapping = true;
    state.velocity = 0;
    setIsSnapping(true);
    setShowHint(false);
    
    const selectAnimate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = config.snapEasing(progress);
      
      state.rotation = startRotation + (endRotation - startRotation) * easedProgress;
      setCarouselRotation(state.rotation);
      
      if (progress < 1) {
        requestAnimationFrame(selectAnimate);
      } else {
        state.isSnapping = false;
        setIsSnapping(false);
        setActiveCardIndex(index);
      }
    };
    
    requestAnimationFrame(selectAnimate);
  };
  
  // Handle card click - using onClick (fires naturally when not dragging)
  const handleCardClick = (e, index) => {
    e.stopPropagation();
    const state = stateRef.current;
    
    // Double-check: only select if we never entered drag mode
    // The browser won't fire onClick if pointer was captured during drag
    if (!state.confirmedDrag) {
      state.lastInteraction = performance.now();
      state.autoplayPaused = true;
      selectCard(index);
    }
  };
  
  // Close detail panel
  const closeDetailPanel = () => {
    setActiveCardIndex(null);
    stateRef.current.autoplayPaused = false;
  };
  
  // Cleanup
  useEffect(() => {
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
      if (autoplayTimeoutRef.current) clearTimeout(autoplayTimeoutRef.current);
    };
  }, []);

  const toggleCard = (id) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  // Recommended tools data
  const toolsData = {
    vpn: [
      {
        name: 'Proton VPN',
        logo: 'https://protonvpn.com/assets/img/favicons/apple-touch-icon.png',
        description: 'Encrypts traffic on public Wi-Fi and reduces casual tracking.',
        tag: 'Free / Paid',
        url: 'https://protonvpn.com'
      },
      {
        name: 'Mullvad VPN',
        logo: 'https://mullvad.net/apple-touch-icon.png',
        description: 'Privacy-first VPN with simple pricing.',
        tag: 'Paid',
        url: 'https://mullvad.net'
      }
    ],
    browser: [
      {
        name: 'Brave',
        logo: 'https://brave.com/static-assets/images/brave-logo-sans-text.svg',
        description: 'Blocks trackers and ads by default.',
        tag: 'Free',
        url: 'https://brave.com'
      },
      {
        name: 'Firefox',
        logo: 'https://www.mozilla.org/media/protocol/img/logos/firefox/browser/logo.eb1324e44442.svg',
        description: 'Strong built-in tracking protection.',
        tag: 'Free',
        url: 'https://www.mozilla.org/firefox'
      },
      {
        name: 'DuckDuckGo',
        logo: 'https://duckduckgo.com/assets/logo_header.v108.svg',
        description: "Private search that doesn't track searches.",
        tag: 'Free',
        url: 'https://duckduckgo.com'
      },
      {
        name: 'Startpage',
        logo: 'https://www.startpage.com/sp/cdn/favicons/apple-touch-icon-180x180.png',
        description: "Private search with an 'Anonymous View' option.",
        tag: 'Free',
        url: 'https://www.startpage.com'
      }
    ],
    email: [
      {
        name: 'Proton Mail',
        logo: 'https://proton.me/favicons/apple-touch-icon.png',
        description: 'Encrypted email option for sensitive messages.',
        tag: 'Free / Paid',
        url: 'https://proton.me/mail'
      },
      {
        name: 'Tuta',
        logo: 'https://tuta.com/resources/favicon/logo-favicon-152.png',
        description: 'Encrypted email plus calendars.',
        tag: 'Free / Paid',
        url: 'https://tuta.com'
      }
    ],
    scam: [
      {
        name: 'Google Safe Browsing',
        logo: 'https://www.google.com/images/branding/product/1x/googleg_48dp.png',
        description: 'Check if a site is flagged as dangerous.',
        tag: 'Free',
        url: 'https://transparencyreport.google.com/safe-browsing/search'
      },
      {
        name: 'VirusTotal',
        logo: 'https://www.virustotal.com/gui/images/manifest/icon-192x192.png',
        description: 'Scan suspicious links/files with multiple engines.',
        tag: 'Optional',
        url: 'https://www.virustotal.com'
      }
    ]
  };

  const toolsTabs = [
    { id: 'vpn', label: 'VPN' },
    { id: 'browser', label: 'Private Browser + Search' },
    { id: 'email', label: 'Encrypted Email' },
    { id: 'scam', label: 'Scam Checks' }
  ];

  // Digital Risk X-Ray Questions
  const xrayQuestions = [
    {
      id: 'q1',
      question: "An unexpected message says 'urgent — verify now'. What do you do?",
      category: 'exposure',
      choices: [
        { id: 'a', text: 'Click and sort it quickly', score: 0, insight: 'Link-pressure messages are designed to rush you.' },
        { id: 'b', text: 'Check the sender + look for inconsistencies', score: 2, insight: null },
        { id: 'c', text: 'Go to the app/site directly (not the link)', score: 3, insight: null },
        { id: 'd', text: 'Not sure', score: 1, insight: 'A quick phishing check can become second nature.' }
      ]
    },
    {
      id: 'q2',
      question: 'Passwords — which sounds most like you?',
      category: 'account',
      choices: [
        { id: 'a', text: 'I reuse a few', score: 0, insight: 'One reuse can turn one breach into many.' },
        { id: 'b', text: 'I vary them, but not everywhere', score: 1, insight: 'Even partial reuse creates risk chains.' },
        { id: 'c', text: 'Unique passwords (mostly)', score: 3, insight: null },
        { id: 'd', text: 'Not sure', score: 1, insight: 'A password check-up takes minutes.' }
      ]
    },
    {
      id: 'q3',
      question: "Have you ever changed your home Wi-Fi router's default settings?",
      category: 'device',
      choices: [
        { id: 'a', text: 'Yes', score: 3, insight: null },
        { id: 'b', text: 'No', score: 0, insight: 'Router defaults are common entry points.' },
        { id: 'c', text: "I don't know", score: 1, insight: 'Router settings are often set once and forgotten.' }
      ]
    },
    {
      id: 'q4',
      question: 'App permissions on your phone — do you review them?',
      category: 'device',
      choices: [
        { id: 'a', text: 'Rarely / never', score: 0, insight: 'Permissions drift quietly over time.' },
        { id: 'b', text: 'Sometimes', score: 1, insight: null },
        { id: 'c', text: 'Yes, occasionally', score: 2, insight: null },
        { id: 'd', text: 'Not sure', score: 1, insight: 'A quick permissions audit can reveal surprises.' }
      ]
    },
    {
      id: 'q5',
      question: 'Account recovery (backup email/phone/2-step) — how set up are you?',
      category: 'account',
      choices: [
        { id: 'a', text: 'Not set', score: 0, insight: 'Recovery is the difference between locked out vs. back in.' },
        { id: 'b', text: 'Partly set', score: 1, insight: 'Partial recovery setup leaves gaps.' },
        { id: 'c', text: 'Fully set', score: 3, insight: null },
        { id: 'd', text: 'Not sure', score: 1, insight: 'Recovery options are easy to check and update.' }
      ]
    },
    {
      id: 'q6',
      question: 'Do you have internet-connected devices at home (cameras, doorbells, smart devices)?',
      category: 'device',
      choices: [
        { id: 'a', text: 'Yes', score: 1, insight: 'Smart devices need occasional tightening too.' },
        { id: 'b', text: 'No', score: 3, insight: null },
        { id: 'c', text: 'Not sure', score: 1, insight: 'Knowing what\'s connected is step one.' }
      ]
    }
  ];

  // Calculate X-Ray Results
  const calculateXrayResults = (answers) => {
    let exposureScore = 0, accountScore = 0, deviceScore = 0;
    let exposureMax = 0, accountMax = 0, deviceMax = 0;
    const insights = [];
    
    xrayQuestions.forEach((q) => {
      const answer = answers[q.id];
      if (answer) {
        const choice = q.choices.find(c => c.id === answer);
        if (choice) {
          const maxScore = Math.max(...q.choices.map(c => c.score));
          
          if (q.category === 'exposure') {
            exposureScore += choice.score;
            exposureMax += maxScore;
          } else if (q.category === 'account') {
            accountScore += choice.score;
            accountMax += maxScore;
          } else if (q.category === 'device') {
            deviceScore += choice.score;
            deviceMax += maxScore;
          }
          
          if (choice.insight) {
            insights.push(choice.insight);
          }
        }
      }
    });
    
    // Normalize to percentage
    const exposure = exposureMax > 0 ? Math.round((exposureScore / exposureMax) * 100) : 50;
    const account = accountMax > 0 ? Math.round((accountScore / accountMax) * 100) : 50;
    const device = deviceMax > 0 ? Math.round((deviceScore / deviceMax) * 100) : 50;
    
    // Determine fastest win
    let fastestWin = '';
    const scores = { exposure, account, device };
    const lowestCategory = Object.entries(scores).sort((a, b) => a[1] - b[1])[0][0];
    
    if (lowestCategory === 'exposure') {
      fastestWin = 'Learn the 3 phishing tells.';
    } else if (lowestCategory === 'account') {
      if (answers.q5 === 'a' || answers.q5 === 'b') {
        fastestWin = 'Turn on 2-step where it matters.';
      } else {
        fastestWin = 'Review your password habits.';
      }
    } else {
      if (answers.q3 === 'b' || answers.q3 === 'c') {
        fastestWin = 'Change router admin password.';
      } else if (answers.q4 === 'a') {
        fastestWin = 'Review 5 app permissions.';
      } else {
        fastestWin = 'Check your connected devices.';
      }
    }
    
    return {
      gauges: {
        exposure: 100 - exposure, // Invert for "risk" display
        account: account,
        device: device
      },
      insights: insights.slice(0, 3),
      fastestWin
    };
  };

  const handleXrayAnswer = (questionId, choiceId) => {
    setXrayAnswers(prev => ({ ...prev, [questionId]: choiceId }));
    
    // Move to next question or complete
    setTimeout(() => {
      if (xrayStep < xrayQuestions.length - 1) {
        setXrayStep(xrayStep + 1);
      } else {
        // Calculate results
        const results = calculateXrayResults({ ...xrayAnswers, [questionId]: choiceId });
        setXrayResults(results);
        setXrayComplete(true);
      }
    }, 400);
  };

  const startXray = () => {
    // Track Lead event when user starts Safety Check
    trackLead({
      lead_type: 'safety_check',
      content_name: 'Digital Safety Check'
    });
    
    setXrayActive(true);
    setXrayStep(0);
    setXrayAnswers({});
    setXrayComplete(false);
    setXrayResults(null);
  };

  const resetXray = () => {
    setXrayActive(false);
    setXrayStep(0);
    setXrayAnswers({});
    setXrayComplete(false);
    setXrayResults(null);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.3,
      rootMargin: '0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisibleSections(prev => new Set(prev).add(entry.target.id));
          
          const heading = entry.target.querySelector('.section-heading');
          if (heading && !heading.classList.contains('animated')) {
            heading.classList.add('animated');
            animateLetters(heading);
          }
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('section[id]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  // Keyword animation observer for "Why All Secured" section
  useEffect(() => {
    if (keywordAnimationTriggered) return;
    
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    const runKeywordAnimation = () => {
      const keywords = ['keyword-clear', 'keyword-action', 'keyword-today'];
      const glowDuration = 800;  // How long each word glows
      const pauseBetween = 700;  // Pause between words
      const pauseBeforeLoop = 2500;  // Pause before restarting the loop
      
      let currentIndex = 0;
      
      const animateNext = () => {
        // Remove glow from all keywords first
        keywords.forEach(id => {
          const el = document.getElementById(id);
          if (el) {
            el.classList.remove('keyword-glow');
            el.classList.add('keyword-dim');
          }
        });
        
        // Add glow to current keyword
        const currentElement = document.getElementById(keywords[currentIndex]);
        if (currentElement) {
          currentElement.classList.remove('keyword-dim');
          currentElement.classList.add('keyword-glow');
        }
        
        // Schedule dim and next
        setTimeout(() => {
          if (currentElement) {
            currentElement.classList.remove('keyword-glow');
            currentElement.classList.add('keyword-dim');
          }
          
          currentIndex++;
          
          if (currentIndex < keywords.length) {
            // Continue to next word after pause
            setTimeout(animateNext, pauseBetween);
          } else {
            // Loop: restart after longer pause
            currentIndex = 0;
            setTimeout(animateNext, pauseBeforeLoop);
          }
        }, glowDuration);
      };
      
      // Start the animation
      animateNext();
    };
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !keywordAnimationTriggered) {
            setKeywordAnimationTriggered(true);
            
            // Start keyword animation loop after initial delay
            setTimeout(() => {
              if (!prefersReducedMotion) {
                runKeywordAnimation();
              }
            }, 600);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (introSectionRef.current) {
      observer.observe(introSectionRef.current);
    }

    return () => observer.disconnect();
  }, [keywordAnimationTriggered]);

  const animateLetters = (element) => {
    const text = element.textContent;
    element.innerHTML = '';
    
    const words = text.split(' ');
    let letterIndex = 0;
    
    words.forEach((word, wordIndex) => {
      const wordSpan = document.createElement('span');
      wordSpan.className = 'word-wrapper';
      wordSpan.style.display = 'inline-block';
      wordSpan.style.whiteSpace = 'nowrap';
      
      word.split('').forEach((char) => {
        const span = document.createElement('span');
        span.textContent = char;
        span.className = 'neon-letter';
        span.style.animationDelay = `${letterIndex * 0.015}s`;
        wordSpan.appendChild(span);
        letterIndex++;
      });
      
      element.appendChild(wordSpan);
      
      if (wordIndex < words.length - 1) {
        const space = document.createElement('span');
        space.innerHTML = '&nbsp;';
        space.className = 'word-space';
        element.appendChild(space);
        letterIndex++;
      }
    });
  };

  // Navigate to section route and scroll to section
  const navigateToSection = (sectionId) => {
    // Special case: "courses" goes to dedicated /courses page
    if (sectionId === 'courses') {
      navigate('/courses');
      return;
    }
    
    // Get the route for this section
    const route = SECTION_TO_ROUTE[sectionId];
    
    // If we're already on the homepage (or a section route), just scroll
    // Otherwise, navigate to the route
    const currentPath = location.pathname;
    const isOnHomepage = currentPath === '/' || ROUTE_TO_SECTION[currentPath];
    
    if (isOnHomepage) {
      // Update URL without full navigation (preserves scroll position during update)
      if (route && route !== '/courses') {
        window.history.pushState({}, '', sectionId === 'hero' ? '/' : route);
      }
      // Scroll to section
      scrollToSection(sectionId);
    } else {
      // Navigate to the section route (this will trigger HomeSectionWrapper)
      navigate(route || '/');
    }
  };

  // Internal scroll function (doesn't change URL)
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 96; // Height of sticky header + small buffer
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: sectionId === 'hero' ? 0 : offsetPosition,
        behavior: 'smooth'
      });
      setIsMenuOpen(false);
    }
  };

  const handleContactFormChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  const handleContactFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${API}/contact`, contactForm);
      
      if (response.status === 200) {
        // Track Contact event on successful form submission
        trackContact({
          content_name: 'Contact Form',
          contact_method: 'form'
        });
        
        setSubmitStatus({
          type: 'success',
          message: 'Thank you for contacting us! We\'ll get back to you soon.'
        });
        setContactForm({ name: '', email: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Sorry, there was an error sending your message. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const navLinks = [
    { label: 'First Step', id: 'hero' },
    { label: 'Baseline', id: 'intro' },
    { label: 'Your Stack', id: 'connected-world' },
    { label: 'Courses', id: 'courses' },
    { label: 'Tools', id: 'tools' },
    { label: 'Testimonials', id: 'testimonials' },
    { label: 'Safety Check', id: 'cta' },
    { label: 'Social', id: 'social' },
    { label: 'Contact', id: 'contact' },
  ];

  // Active section tracking for nav highlighting
  const [activeSection, setActiveSection] = useState('hero');

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const headerOffset = 100; // Account for sticky header
      const scrollPosition = window.scrollY + headerOffset;
      
      // Get all section positions
      const sections = navLinks.map(link => {
        const element = document.getElementById(link.id);
        if (element) {
          return {
            id: link.id,
            top: element.offsetTop,
            bottom: element.offsetTop + element.offsetHeight
          };
        }
        return null;
      }).filter(Boolean);
      
      // Find the current section
      for (let i = sections.length - 1; i >= 0; i--) {
        if (scrollPosition >= sections[i].top - 50) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Structured data for homepage (Organization + WebSite)
  const homeStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "name": "All Secured",
        "url": "https://allsecured.com.au",
        "logo": "https://customer-assets.emergentagent.com/job_0e5aee2f-27de-4cc5-bc22-1ba27f01672b/artifacts/f8dzsnko_Variant%201_Shield%20Only_Vector.png",
        "description": "Practical digital safety and cyber privacy education for everyday people",
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer support",
          "url": "https://allsecured.com.au/contact"
        }
      },
      {
        "@type": "WebSite",
        "name": "All Secured",
        "url": "https://allsecured.com.au",
        "description": "Practical digital safety and cyber privacy education for everyday people. Stop silent tracking, reduce profiling, prevent scams, and protect your digital life step by step.",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://allsecured.com.au/courses?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      }
    ]
  };

  return (
    <div className="home-page">
      <SEO 
        title="Digital Safety & Cyber Privacy Courses | All Secured"
        description="Practical digital safety and cyber privacy education for everyday people. Stop silent tracking, reduce profiling, prevent scams, and protect your digital life step by step."
        canonical="/"
        structuredData={homeStructuredData}
      />
      
      {/* Fixed Header */}
      <header className={`site-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="header-content">
          <div onClick={() => navigateToSection('hero')} style={{ cursor: 'pointer' }}>
            <AnimatedLogo />
          </div>
          
          {/* Desktop Navigation */}
          <nav className="desktop-nav">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => navigateToSection(link.id)}
                className={`nav-link ${activeSection === link.id ? 'nav-active' : ''}`}
                data-testid={`nav-${link.id}`}
              >
                {link.label}
              </button>
            ))}

            {/* Scam IQ badge — shows after the buyer completes their first test. */}
            <ScamIQBadge />

            <Link
              to="/business-ai-cyber-support"
              className="nav-link nav-auth-link"
              data-testid="home-nav-business"
              onClick={() => window.scrollTo(0, 0)}
            >
              For Business
            </Link>
            
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="nav-link nav-auth-link">
                  Dashboard
                </Link>
                <button onClick={logout} className="nav-link nav-logout">
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="nav-link nav-auth-link">
                  Login
                </Link>
                <Link to="/signup" className="nav-link nav-signup">
                  Sign Up
                </Link>
              </>
            )}
          </nav>

          {/* Mobile Menu Toggle */}
          <button 
            className="mobile-menu-toggle"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="mobile-nav">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => { navigateToSection(link.id); setIsMenuOpen(false); }}
                className={`mobile-nav-link ${activeSection === link.id ? 'mobile-nav-active' : ''}`}
                data-testid={`mobile-nav-${link.id}`}
              >
                {link.label}
              </button>
            ))}
            
            {isAuthenticated ? (
              <>
                <Link 
                  to="/dashboard" 
                  className="mobile-nav-link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <button 
                  onClick={() => { logout(); setIsMenuOpen(false); }} 
                  className="mobile-nav-link mobile-logout"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/business-ai-cyber-support"
                  className="mobile-nav-link"
                  onClick={() => { setIsMenuOpen(false); window.scrollTo(0, 0); }}
                  data-testid="home-mobile-nav-business"
                >
                  For Business
                </Link>
                <Link 
                  to="/login" 
                  className="mobile-nav-link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Login
                </Link>
                <Link 
                  to="/signup" 
                  className="mobile-nav-link mobile-signup"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <div className="hero-video-container">
          <video 
            ref={heroVideoRef}
            className="hero-video"
            autoPlay 
            loop 
            muted 
            playsInline
            preload="auto"
            poster="https://customer-assets.emergentagent.com/job_dd270f66-166f-410f-97b9-46a7b8361e75/artifacts/bor27kj8_ChatGPT%20Image%20Feb%2021%2C%202026%20at%2011_53_14%20AM.png"
            disablePictureInPicture
            controlsList="nodownload nofullscreen noremoteplayback"
          >
            <source 
              src="https://customer-assets.emergentagent.com/job_dd270f66-166f-410f-97b9-46a7b8361e75/artifacts/3860f50l_Use_the_uploaded_lake_image_as_Seedance_15_Pro_22595.mp4" 
              type="video/mp4" 
            />
          </video>
          
          <div className="hero-overlay">
            <div className="hero-content">
              <h1 className="hero-heading">
                <span className="headline-glow">A Safer Digital World — until it's second nature.</span>
              </h1>
              <p className="hero-subheading">Feel the progress as you go — clear steps, checkpoints, and routines that fit real life.</p>
              
              <div className="hero-buttons">
                <Link to="/courses" className="btn-primary">
                  Explore Courses
                </Link>
                <button className="btn-secondary" onClick={() => navigateToSection('intro')}>
                  Why All Secured?
                </button>
              </div>
              
              <p className="hero-support-text">Your World. Protected.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section id="intro" className="intro-section" ref={introSectionRef}>
        <div className="container">
          <span className="section-label">Why All Secured</span>
          <h2 className="section-heading intro-headline">Guided Action. Habits That Stick.</h2>
          
          <div className="intro-content">
            <p className="intro-text">
              All Secured is for the moment you realise, "I should probably tighten this up," and want a <span id="keyword-clear" className="keyword">clear</span> way forward.
            </p>
            
            <p className="intro-text">
              No noise. No jargon. No pressure to become technical.
            </p>
            
            <p className="intro-text">
              With step-by-step guidance and built-in checkpoints, we help you take <span id="keyword-action" className="keyword">action</span> — and turn those actions into habits. Over time, you stop relying on the standard settings and device configurations you never chose and start making intentional choices, almost without thinking about it, starting <span id="keyword-today" className="keyword">today</span>.
            </p>
            
            <ul className="intro-bullets">
              <li>Designed for all levels of digital literacy</li>
              <li>Real protections applied as you go — with clear double-checks built in</li>
              <li>Any internet-connected device — we show you how to tighten its settings and configurations to protect your privacy and security</li>
            </ul>
            
            <div className="intro-cta">
              <Link to="/courses" className="btn-primary intro-cta-btn" data-testid="intro-lets-start-btn">
                Let's Start
                <ArrowRight size={20} className="btn-arrow" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Family Products — Handbook + ScamSmart (coming soon) */}
      <FamilyProductsSection />

      <PerthPropertyPulseTile />

      {/* Device Track Carousel Section - 3D Perspective */}
      <section id="connected-world" className="carousel-3d-section">
        {/* Background visual elements */}
        <div className="carousel-bg-grid" />
        <div className="carousel-particles">
          <div className="carousel-particle" />
          <div className="carousel-particle" />
          <div className="carousel-particle" />
          <div className="carousel-particle" />
          <div className="carousel-particle" />
          <div className="carousel-particle" />
          <div className="carousel-particle" />
        </div>
        
        <div className="container">
          <div className="carousel-3d-header">
            <h2 className="section-heading">Take a Quick Tour of Your Digital Life</h2>
            <p className="carousel-3d-subheading">
              Drag to explore — tap any device to see how we help secure it.
            </p>
          </div>
          
          {/* 3D Carousel Stage */}
          <div 
            className={`carousel-3d-stage ${isDragging ? 'is-dragging' : ''}`}
            ref={carouselRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            onPointerLeave={handlePointerUp}
          >
            {/* Center stage glow */}
            <div className="carousel-stage-glow" />
            
            <div 
              className={`carousel-3d-track ${isSnapping ? 'snapping' : ''} ${isDragging ? 'dragging' : ''}`}
              data-testid="device-track"
            >
              {trackDevices.map((device, index) => {
                const IconComponent = device.icon;
                const isActive = activeCardIndex === index;
                const transform = getCardTransform(index, carouselRotation, isActive);
                const isSnapTarget = snapCardIndex === index;
                
                return (
                  <div
                    key={device.id}
                    className={`carousel-3d-card ${isActive ? 'active' : ''} ${transform.isCenter ? 'center' : ''} ${isSnapTarget ? 'snap-active' : ''}`}
                    style={{
                      transform: `
                        translate3d(${transform.x}px, 0, ${transform.z}px)
                        rotateY(${transform.rotateY}deg)
                        scale(${transform.scale})
                      `,
                      opacity: transform.opacity,
                      filter: `brightness(${transform.brightness})`,
                      zIndex: transform.isCenter ? 10 : Math.round(5 - Math.abs(transform.z) / 50)
                    }}
                    onClick={(e) => handleCardClick(e, index)}
                    data-testid={`track-card-${device.id}`}
                  >
                    <div className="carousel-3d-card-inner">
                      <div className="carousel-3d-card-reflection" />
                      <div className="carousel-3d-card-icon-wrap">
                        <IconComponent size={32} strokeWidth={1.5} className="carousel-3d-card-icon" />
                      </div>
                      <h4 className="carousel-3d-card-title">{device.label}</h4>
                      <p className="carousel-3d-card-teaser">{device.teaser}</p>
                    </div>
                    {isActive && <div className="carousel-3d-card-glow" />}
                    <div className="carousel-3d-card-snap-ring" />
                    <div className="carousel-3d-card-shadow" />
                  </div>
                );
              })}
            </div>
            
            {/* Center spotlight effect */}
            <div className="carousel-3d-spotlight" />
            
            {/* Floor reflection */}
            <div className="carousel-3d-floor" />
            
            {/* Drag hint tooltip */}
            <div className={`carousel-drag-hint ${!showDragHint ? 'hidden' : ''}`}>
              <GripHorizontal size={16} />
              <span>Drag to explore</span>
            </div>
          </div>
          
          {/* Hint */}
          {showHint && activeCardIndex === null && (
            <p className="carousel-3d-hint">
              Tap on <span>{hintDevice}</span> to learn more
            </p>
          )}
          
          {/* Detail Panel */}
          {activeCardIndex !== null && (
            <div className="track-detail-panel" data-testid="track-detail-panel">
              {(() => {
                const device = trackDevices[activeCardIndex];
                const IconComponent = device.icon;
                
                return (
                  <>
                    {/* Close button */}
                    <button 
                      className="track-detail-close" 
                      onClick={closeDetailPanel}
                      aria-label="Close panel"
                      data-testid="track-detail-close"
                    >
                      <X size={20} />
                    </button>
                    
                    {/* Watermark illustration */}
                    <div className="track-detail-watermark">
                      <IconComponent size={120} strokeWidth={0.5} />
                    </div>
                    
                    <div className="track-detail-header">
                      <div className="track-detail-icon">
                        <IconComponent size={32} strokeWidth={1.5} />
                      </div>
                      <div className="track-detail-title-area">
                        <h3 className="track-detail-title">{device.label}</h3>
                        {!device.isRelevant && (
                          <span className="track-coming-soon-badge">
                            <Clock size={12} />
                            Coming soon
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <p className="track-detail-benefit">{device.benefit}</p>
                    <p className="track-detail-pain">{device.painLine}</p>
                    
                    <ul className="track-detail-bullets">
                      {device.bullets.map((bullet, i) => (
                        <li key={i}>
                          <Check size={16} />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <div className="track-detail-did-you-know">
                      <div className="dyk-icon">
                        <Lightbulb size={16} />
                      </div>
                      <div className="dyk-content">
                        <span className="dyk-label">Did you know?</span>
                        <span className="dyk-text">{device.didYouKnow}</span>
                      </div>
                    </div>
                    
                    <div className="track-detail-cta-area">
                      {device.isRelevant ? (
                        <Link to="/courses/cyber-privacy-security-basics" className="track-detail-link" data-testid="track-detail-link">
                          See how the course covers this
                          <ArrowRight size={16} />
                        </Link>
                      ) : (
                        <div className="track-detail-coming-soon">
                          <span className="coming-soon-pill">Coming soon</span>
                          <span className="coming-soon-text">Covered in upcoming courses</span>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </section>

      {/* Courses Section */}
      <section id="courses" className="courses-section">
        <div className="container">
          <h2 className="section-heading">Courses Built for Immediate Action.</h2>
          <p className="courses-subheading">Follow guided walkthroughs that turn security into progress you can see — and habits that last.</p>
          
          {/* Hero Course Card */}
          <div className="hero-course-wrapper">
            <Link to="/courses/cyber-privacy-security-basics" className="hero-course-card" data-testid="hero-course-card">
              <div className="hero-course-image">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/1c6c6b3a-1868-4912-a311-b4e13c732a68/images/831d2358fd54847d6ac9ec8aba5691b524b6120b0d9bc8dcda9b27a7010d1d83.png" 
                  alt="Privacy & Security Basics" 
                  loading="lazy"
                />
              </div>
              <div className="hero-course-content">
                <span className="course-tag start-here">Start here</span>
                <h3 className="hero-course-title">Privacy & Security Basics</h3>
                <p className="hero-course-description">
                  A guided reset for the settings that quietly shape your risk — across your accounts, apps, and devices. Make smart changes in one sitting, with clear "this is correct" checks along the way.
                </p>
                <span className="hero-course-cta">
                  Start the course
                  <ArrowRight size={18} />
                </span>
              </div>
            </Link>
          </div>
          
          {/* Coming Soon Courses Grid */}
          <div className="coming-soon-grid">
            {/* Wi-Fi Networks Card */}
            <div className="coming-soon-card" data-testid="course-wifi">
              <div className="coming-soon-image">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/1c6c6b3a-1868-4912-a311-b4e13c732a68/images/97f327cfe84f36d221237bdd2dd6e47db02594b91e513d3616e5e863d8235ad0.png" 
                  alt="Securing Wi-Fi Networks" 
                  loading="lazy"
                />
                <div className="coming-soon-image-overlay" />
              </div>
              <div className="coming-soon-content">
                <div className="coming-soon-header">
                  <h4 className="coming-soon-title">Securing Wi-Fi Networks</h4>
                  <span className="course-tag coming-soon-badge">
                    <Lock size={12} />
                    Coming soon
                  </span>
                </div>
                <p className="coming-soon-description">
                  Lock down your router and home network so outsiders can't piggyback on your connection — or use it as a pathway to what's inside your home.
                </p>
              </div>
            </div>
            
            {/* Phishing & Scam Defence Card */}
            <div className="coming-soon-card" data-testid="course-phishing">
              <div className="coming-soon-image">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/1c6c6b3a-1868-4912-a311-b4e13c732a68/images/7e6f0a7e734f039d91f0b19d739515b4f2d46a99ea947a886dbd609f50cdb0e4.png" 
                  alt="Phishing & Scam Defence" 
                  loading="lazy"
                />
                <div className="coming-soon-image-overlay" />
              </div>
              <div className="coming-soon-content">
                <div className="coming-soon-header">
                  <h4 className="coming-soon-title">Phishing & Scam Defence</h4>
                  <span className="course-tag coming-soon-badge">
                    <Lock size={12} />
                    Coming soon
                  </span>
                </div>
                <p className="coming-soon-description">
                  Spot fakes fast, recognise common scam patterns, and build simple checking habits — so you know where to look before you click, pay, or share details.
                </p>
              </div>
            </div>
            
            {/* Small Business Security Card */}
            <div className="coming-soon-card" data-testid="course-business">
              <div className="coming-soon-image">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/1c6c6b3a-1868-4912-a311-b4e13c732a68/images/70faf542a07aa17e3bd6b591305b7df2cfd7cae4ee3575c85e68beaf045ef55d.png" 
                  alt="Small Business Security Essentials" 
                  loading="lazy"
                />
                <div className="coming-soon-image-overlay" />
              </div>
              <div className="coming-soon-content">
                <div className="coming-soon-header">
                  <h4 className="coming-soon-title">Small Business Security Essentials</h4>
                  <span className="course-tag coming-soon-badge">
                    <Lock size={12} />
                    Coming soon
                  </span>
                </div>
                <p className="coming-soon-description">
                  Practical protections for small teams — safer logins, permissions, device basics, and everyday policies you can actually maintain.
                </p>
              </div>
            </div>
          </div>
          
          <div className="courses-cta">
            <Link to="/courses" className="btn-primary" data-testid="view-all-courses-btn">
              View All Courses
            </Link>
          </div>
        </div>
      </section>

      {/* Recommended Tools Section */}
      <section id="tools" className="tools-section">
        <div className="container">
          <div className="tools-header">
            <h2 className="section-heading">Recommended tools</h2>
            <p className="tools-subheading">Optional tools we reference and recommend — so you can take action immediately.</p>
            <span className="tools-helper">No affiliation. No hype.</span>
          </div>
          
          {/* Tabs */}
          <div className="tools-tabs">
            {toolsTabs.map((tab) => (
              <button
                key={tab.id}
                className={`tools-tab ${activeToolsTab === tab.id ? 'active' : ''}`}
                onClick={() => {
                  setActiveToolsTab(tab.id);
                  setShowMoreBrowser(false);
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          
          {/* Cards Grid */}
          <div className="tools-cards-grid">
            {(activeToolsTab === 'browser' && !showMoreBrowser 
              ? toolsData[activeToolsTab].slice(0, 3) 
              : toolsData[activeToolsTab]
            ).map((tool, index) => (
              <a
                key={tool.name}
                href={tool.url}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="tool-card"
                data-testid={`tool-card-${tool.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="tool-logo-wrapper">
                  <img 
                    src={tool.logo} 
                    alt={`${tool.name} logo`} 
                    className="tool-logo"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="tool-logo-fallback" style={{ display: 'none' }}>
                    {tool.name.charAt(0)}
                  </div>
                </div>
                <div className="tool-content">
                  <div className="tool-name-row">
                    <h4 className="tool-name">{tool.name}</h4>
                    <ExternalLink size={14} className="tool-external-icon" />
                  </div>
                  <p className="tool-description">{tool.description}</p>
                  <span className={`tool-tag ${tool.tag.toLowerCase().includes('paid') ? 'paid' : tool.tag.toLowerCase() === 'optional' ? 'optional' : 'free'}`}>
                    {tool.tag}
                  </span>
                </div>
              </a>
            ))}
          </div>
          
          {/* View More button for Browser tab */}
          {activeToolsTab === 'browser' && !showMoreBrowser && toolsData.browser.length > 3 && (
            <div className="tools-view-more">
              <button 
                className="view-more-btn"
                onClick={() => setShowMoreBrowser(true)}
              >
                View more
              </button>
            </div>
          )}
          
          {/* Optional message */}
          <p className="tools-optional-message">
            These tools are optional — the course works without buying extra software.
          </p>
          
          {/* Disclaimer */}
          <p className="tools-disclaimer">
            Third-party trademarks and logos belong to their respective owners. Listing does not imply endorsement or partnership.
          </p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials-section">
        <div className="container">
          <div className="testimonials-header">
            <h2 className="section-heading">Real Results. Real People.</h2>
            <p className="testimonials-subheading">Not theory — real changes people made, and the mindset they left with.</p>
          </div>
          
          <div className="testimonials-grid">
            <div className="testimonial-card" data-testid="testimonial-1">
              <div className="testimonial-accent-line" />
              <Quote className="testimonial-quote-icon" size={28} />
              <span className="testimonial-tag">Mindset shift</span>
              <p className="testimonial-quote">
                "This course did something I didn't expect — it gave me real control back. I always knew there were settings and safeguards in my accounts, but I didn't know where they lived or how to use them properly. Now I do — and I actually think about those controls when I use apps in general.
                <br /><br />
                It also sharpened my instincts: if something feels off, it usually is. That mindset alone is worth it."
              </p>
              <p className="testimonial-author">— Capt. B. George</p>
            </div>
            
            <div className="testimonial-card" data-testid="testimonial-2">
              <div className="testimonial-accent-line" />
              <Quote className="testimonial-quote-icon" size={28} />
              <span className="testimonial-tag">Professional perspective</span>
              <p className="testimonial-quote">
                "Cyber privacy is going to be the next big shift — and most people are unprepared because they don't realise how much they're giving away by default. This course builds the right kind of awareness: the kind that gives people back the power to decide what apps and devices can do with their personal space.
                <br /><br />
                As an advisor, I tell clients the same thing: start forming the habits today. This makes it easy to begin."
              </p>
              <p className="testimonial-author">— Alex Croydon, <span>Financial Advisor</span></p>
            </div>
            
            <div className="testimonial-card" data-testid="testimonial-3">
              <div className="testimonial-accent-line" />
              <Quote className="testimonial-quote-icon" size={28} />
              <span className="testimonial-tag">Easy to follow</span>
              <p className="testimonial-quote">
                "I genuinely enjoyed it. It was clear, easy to follow, and it kept moving — but never rushed. What I loved most is that it confirms you've actually done things correctly, step by step.
                <br /><br />
                As a teacher, that's exactly how I like information presented: understood, applied, and checked."
              </p>
              <p className="testimonial-author">— Patricia Collins, <span>Teacher</span></p>
            </div>
          </div>
        </div>
      </section>

      {/* Digital Risk X-Ray Section */}
      <section id="cta" className="xray-section">
        {/* Animated Background Elements */}
        <div className="xray-bg-container">
          {/* Grid Pattern */}
          <div className="xray-grid-pattern" />
          
          {/* Horizontal Scan Line */}
          <div className="xray-scan-sweep" />
          
          {/* Data Lines */}
          <div className="xray-data-lines">
            <div className="xray-data-line" />
            <div className="xray-data-line" />
            <div className="xray-data-line" />
            <div className="xray-data-line" />
          </div>
          
          {/* Floating Particles */}
          <div className="xray-particles">
            {[...Array(25)].map((_, i) => (
              <div 
                key={i} 
                className="xray-particle"
                style={{
                  left: `${5 + Math.random() * 90}%`,
                  top: `${5 + Math.random() * 90}%`,
                  animationDelay: `${Math.random() * 8}s, ${Math.random() * 3}s`,
                  animationDuration: `${12 + Math.random() * 8}s, ${2 + Math.random() * 2}s`
                }}
              />
            ))}
          </div>
          
          {/* Device Constellation Icons - LARGER & MORE VISIBLE */}
          <div className="xray-constellation">
            <Smartphone className="xray-constellation-icon icon-1" size={42} strokeWidth={1.2} />
            <Wifi className="xray-constellation-icon icon-2" size={48} strokeWidth={1.2} />
            <Camera className="xray-constellation-icon icon-3" size={38} strokeWidth={1.2} />
            <Cloud className="xray-constellation-icon icon-4" size={46} strokeWidth={1.2} />
            <Mail className="xray-constellation-icon icon-5" size={40} strokeWidth={1.2} />
            <Shield className="xray-constellation-icon icon-6" size={50} strokeWidth={1.2} />
          </div>
        </div>
        
        <div className="container">
          {!xrayActive ? (
            /* Initial State - CTA to start */
            <div className="xray-intro">
              <span className="section-label">Last thing before you go</span>
              <h2 className="xray-heading">Run a 60-Second Digital Safety Check</h2>
              <p className="xray-subheading">
                Most people are one setting away from a problem — not because they're careless, but because nobody showed them where to look.
              </p>
              <p className="xray-support">Get your snapshot now. We'll show you the exact steps inside the courses.</p>
              
              <div className="xray-cta-buttons">
                <button 
                  onClick={startXray} 
                  className="btn-primary xray-start-btn"
                  data-testid="xray-start-btn"
                >
                  <span className="xray-btn-ping" />
                  <Scan size={20} />
                  Start the Safety Check
                </button>
                <Link to="/courses" className="xray-secondary-link">
                  View Courses
                </Link>
              </div>
            </div>
          ) : !xrayComplete ? (
            /* Question State */
            <div className="xray-app" data-testid="xray-app">
              <div className="xray-scan-lines" />
              
              <div className="xray-progress">
                <div className="xray-progress-text">
                  <Scan size={16} className="xray-scan-icon" />
                  <span>{xrayStep + 1} of {xrayQuestions.length}</span>
                </div>
                <div className="xray-progress-bar">
                  <div 
                    className="xray-progress-fill" 
                    style={{ width: `${((xrayStep + 1) / xrayQuestions.length) * 100}%` }}
                  />
                </div>
              </div>
              
              <div className="xray-question-container">
                <h3 className="xray-question">{xrayQuestions[xrayStep].question}</h3>
                
                <div className="xray-choices">
                  {xrayQuestions[xrayStep].choices.map((choice) => (
                    <button
                      key={choice.id}
                      className={`xray-choice ${xrayAnswers[xrayQuestions[xrayStep].id] === choice.id ? 'selected' : ''}`}
                      onClick={() => handleXrayAnswer(xrayQuestions[xrayStep].id, choice.id)}
                      data-testid={`xray-choice-${choice.id}`}
                    >
                      <span className="xray-choice-indicator">{choice.id.toUpperCase()}</span>
                      <span className="xray-choice-text">{choice.text}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              <button onClick={resetXray} className="xray-cancel-btn">
                <X size={16} />
                Cancel
              </button>
            </div>
          ) : (
            /* Results State */
            <div className="xray-results" data-testid="xray-results">
              <div className="xray-results-header">
                <div className="xray-complete-icon">
                  <CheckCircle2 size={32} />
                </div>
                <h3 className="xray-results-title">Scan Complete</h3>
                <p className="xray-results-subtitle">Your Risk Snapshot</p>
              </div>
              
              <div className="xray-gauges">
                <div className="xray-gauge">
                  <div className="xray-gauge-visual">
                    <svg viewBox="0 0 100 100" className="xray-gauge-svg">
                      <circle cx="50" cy="50" r="40" className="xray-gauge-bg" />
                      <circle 
                        cx="50" 
                        cy="50" 
                        r="40" 
                        className="xray-gauge-fill exposure"
                        style={{ 
                          strokeDasharray: `${(xrayResults?.gauges.exposure || 0) * 2.51} 251`,
                          strokeDashoffset: 62.75
                        }}
                      />
                    </svg>
                    <span className="xray-gauge-value">{xrayResults?.gauges.exposure || 0}%</span>
                  </div>
                  <span className="xray-gauge-label">Exposure</span>
                </div>
                
                <div className="xray-gauge">
                  <div className="xray-gauge-visual">
                    <svg viewBox="0 0 100 100" className="xray-gauge-svg">
                      <circle cx="50" cy="50" r="40" className="xray-gauge-bg" />
                      <circle 
                        cx="50" 
                        cy="50" 
                        r="40" 
                        className="xray-gauge-fill account"
                        style={{ 
                          strokeDasharray: `${(xrayResults?.gauges.account || 0) * 2.51} 251`,
                          strokeDashoffset: 62.75
                        }}
                      />
                    </svg>
                    <span className="xray-gauge-value">{xrayResults?.gauges.account || 0}%</span>
                  </div>
                  <span className="xray-gauge-label">Account Safety</span>
                </div>
                
                <div className="xray-gauge">
                  <div className="xray-gauge-visual">
                    <svg viewBox="0 0 100 100" className="xray-gauge-svg">
                      <circle cx="50" cy="50" r="40" className="xray-gauge-bg" />
                      <circle 
                        cx="50" 
                        cy="50" 
                        r="40" 
                        className="xray-gauge-fill device"
                        style={{ 
                          strokeDasharray: `${(xrayResults?.gauges.device || 0) * 2.51} 251`,
                          strokeDashoffset: 62.75
                        }}
                      />
                    </svg>
                    <span className="xray-gauge-value">{xrayResults?.gauges.device || 0}%</span>
                  </div>
                  <span className="xray-gauge-label">Device Hygiene</span>
                </div>
              </div>
              
              {xrayResults?.insights.length > 0 && (
                <div className="xray-insights">
                  <h4 className="xray-insights-title">
                    <AlertTriangle size={18} />
                    Top blind spots we found
                  </h4>
                  <ul className="xray-insights-list">
                    {xrayResults.insights.map((insight, i) => (
                      <li key={i}>{insight}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="xray-fastest-win">
                <div className="xray-fastest-win-header">
                  <Zap size={18} />
                  <span>Your fastest win today</span>
                </div>
                <p className="xray-fastest-win-text">{xrayResults?.fastestWin}</p>
              </div>
              
              <div className="xray-reassurance">
                <p>You'll get the exact steps — plus clear double-checks — inside the courses.</p>
              </div>
              
              <div className="xray-results-cta">
                <Link to="/courses/cyber-privacy-security-basics" className="btn-primary xray-course-btn" data-testid="xray-course-btn">
                  Start Privacy & Security Basics
                  <ArrowRight size={18} />
                </Link>
                <Link to="/courses" className="xray-view-all-link">
                  View All Courses
                </Link>
              </div>
              
              <button onClick={resetXray} className="xray-retake-btn">
                <RotateCcw size={14} />
                Take again
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Social Links Section */}
      <section id="social" className="social-section">
        <div className="container">
          <p className="social-heading">Follow us for ongoing tips, updates, and bite-sized cybersecurity wisdom.</p>
          
          <div className="social-icons">
            <a href="https://www.facebook.com/profile.php?id=61583809348725" className="social-link" title="Facebook" target="_blank" rel="noopener noreferrer">
              <Facebook size={32} />
            </a>
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <span className="social-link social-link-coming-soon" title="Instagram">
                    <Instagram size={32} />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="coming-soon-tooltip">
                  <p>Something Exciting Coming Soon ✨</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <span className="social-link social-link-coming-soon" title="YouTube">
                    <Youtube size={32} />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="coming-soon-tooltip">
                  <p>Something Exciting Coming Soon ✨</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <a href="https://www.linkedin.com/company/all-secured/posts/?feedView=all&viewAsMember=true" className="social-link" title="LinkedIn" target="_blank" rel="noopener noreferrer">
              <Linkedin size={32} />
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="container">
          <h2 className="section-heading">We're Here to Help.</h2>
          <p className="contact-subheading">Have questions? Need guidance? Reach out anytime.</p>
          
          <form className="contact-form" onSubmit={handleContactFormSubmit}>
            {submitStatus.message && (
              <div className={`form-status ${submitStatus.type}`}>
                {submitStatus.message}
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input 
                type="text" 
                id="name" 
                name="name" 
                value={contactForm.name}
                onChange={handleContactFormChange}
                required 
                className="form-input"
                disabled={isSubmitting}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input 
                type="email" 
                id="email" 
                name="email" 
                value={contactForm.email}
                onChange={handleContactFormChange}
                required 
                className="form-input"
                disabled={isSubmitting}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea 
                id="message" 
                name="message" 
                rows="5" 
                value={contactForm.message}
                onChange={handleContactFormChange}
                required 
                className="form-textarea"
                disabled={isSubmitting}
              ></textarea>
            </div>
            
            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer id="footer" className="site-footer">
        <div className="container">
          <div className="footer-content">
            <img 
              src="https://customer-assets.emergentagent.com/job_accordion-repair/artifacts/06uiebgi_Variant%201_Full%20Logo_Vector%20copy%202.png" 
              alt="All Secured Logo" 
              className="footer-logo"
            />
            
            <div className="footer-links">
              <h4>Quick Links</h4>
              <Link to="/courses">Courses</Link>
              <Link to="/login">Login</Link>
              <Link to="/signup">Sign Up</Link>
            </div>
            
            <div className="footer-links">
              <h4>Products</h4>
              <Link to="/course-1-blueprint" onClick={() => window.scrollTo(0, 0)}>Course 1 Blueprint</Link>
              <Link to="/parents-handbook" onClick={() => window.scrollTo(0, 0)}>Parents' Handbook</Link>
              <Link to="/scamsmart" onClick={() => window.scrollTo(0, 0)}>ScamSmart</Link>
            </div>

            <div className="footer-links">
              <h4>For Business</h4>
              <Link to="/business-ai-cyber-support" onClick={() => window.scrollTo(0, 0)} data-testid="footer-business-link">AI &amp; Cyber Safety Support</Link>
            </div>
            
            <div className="footer-links">
              <h4>Legal</h4>
              <Link to="/privacy-policy">Privacy Policy</Link>
              <Link to="/terms-of-use">Terms of Use</Link>
              <Link to="/refund-policy">Refund Policy</Link>
              <Link to="/cookie-policy">Cookie Policy</Link>
              <Link to="/security-statement">Security Statement</Link>
            </div>
            
            {/* Reopen Hunt Link */}
            {CYBER_HUNT_ENABLED && (
              <div className="footer-links">
                <h4>Bonus</h4>
                <button 
                  onClick={reopenHuntModal} 
                  className="footer-hunt-link"
                >
                  <Gift size={14} />
                  {hasCompletedHunt ? 'Play Again' : 'Play Cyber Forest Hunt'}
                </button>
              </div>
            )}
          </div>
          
          <div className="footer-bottom">
            <p>&copy; 2025 All Secured. All rights reserved.</p>
            <p className="footer-stripe-notice">Secure payments powered by Stripe.</p>
          </div>
        </div>
      </footer>

      {/* Cyber Forest Hunt Modal */}
      {CYBER_HUNT_ENABLED && (
        <CyberForestHuntModal
          isOpen={showHuntModal}
          onClose={closeHuntModal}
          onComplete={(code) => {
            markHuntCompleted();
            console.log('Hunt completed with code:', code);
          }}
          courseSlug="cyber-privacy-security-basics"
        />
      )}

      {/* Cyber Forest Hunt Banner */}
      {CYBER_HUNT_ENABLED && showHuntBanner && (
        <CyberForestHuntBanner
          onPlay={reopenHuntModal}
          onClose={closeHuntBanner}
        />
      )}
    </div>
  );
};

export default Home;
